import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  cancelarImportacaoAcervo,
  fetchAcervoStatus,
  importarAcervo,
  type AcervoImportProgress,
} from '../api/acervo';
import { queryKeys } from '../lib/pwa/queryConfig';
import { useJobProgress } from './useJobProgress';
import { getAtuacao } from '@/lib/delegacao/atuacao';

const ATIVOS = new Set(['PENDING', 'RUNNING']);
/** Tempo que o aviso de import cancelado fica na tela antes de sair sozinho. */
const AVISO_CANCELADO_MS = 5000;
const CHAVE_DISPENSADO = 'acervo-import:dispensado';

// O resumo de um import fica disponível por 10 min; o aviso fechado não volta ao reabrir a tela.
function jobDispensado(): string | null {
  try {
    return sessionStorage.getItem(CHAVE_DISPENSADO);
  } catch {
    return null;
  }
}

function lembrarDispensado(jobId: string | undefined) {
  try {
    if (jobId) sessionStorage.setItem(CHAVE_DISPENSADO, jobId);
  } catch {
    // Sem storage (aba privada): o aviso só não fica lembrado entre visitas.
  }
}

/**
 * Estado do import do acervo na tela de Processos.
 *
 * Ao montar, pergunta ao backend se há import na fila ou rodando (o automático da conexão à
 * PDPJ, por exemplo) ou concluído há pouco, e acompanha por SSE. Falha de import automático não
 * aparece: só a do botão, que o usuário acabou de pedir. Ao concluir, recarrega processos e
 * clientes.
 */
export function useAcervoImport() {
  const queryClient = useQueryClient();
  const [acompanhar, setAcompanhar] = useState(false);
  const [geracao, setGeracao] = useState(0);
  const [dispensado, setDispensado] = useState<string | null>(jobDispensado);
  // Estado já conhecido (status do backend, ou o pedido que o usuário acabou de fazer): pinta o
  // aviso na hora, sem esperar o handshake do SSE.
  const [conhecido, setConhecido] = useState<AcervoImportProgress | null>(null);
  const [cancelouAtivo, setCancelouAtivo] = useState(false);
  const statusAnterior = useRef<string | undefined>(undefined);
  /** Pedido de import ainda em voo: o cancelamento espera por ele (ver `cancelar`). */
  const pedidoEmVoo = useRef<Promise<unknown> | undefined>(undefined);
  const progresso = useJobProgress<AcervoImportProgress>(
    acompanhar,
    '/processos/acervo/stream',
    fetchAcervoStatus,
    geracao,
    conhecido,
  );

  useEffect(() => {
    // Na conta de outra pessoa o import do acervo é do titular (rota não delegável).
    if (getAtuacao()) return;
    let ativo = true;
    fetchAcervoStatus()
      .then((estado) => {
        if (!ativo || estado.status === 'idle') return;
        setConhecido(estado);
        setAcompanhar(true);
      })
      .catch(() => {
        // Sem status (offline, backend fora): o card simplesmente não aparece.
      });
    return () => {
      ativo = false;
    };
  }, []);

  // Recarrega processos e clientes só quando ESTE acompanhamento viu o import terminar — não a
  // cada volta à tela com um resumo antigo ainda no Redis.
  useEffect(() => {
    const anterior = statusAnterior.current;
    statusAnterior.current = progresso?.status;
    if (progresso?.status === 'COMPLETED' && anterior && ATIVOS.has(anterior)) {
      queryClient.invalidateQueries({ queryKey: queryKeys.processos.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.clientes.all });
    }
  }, [progresso?.status, queryClient]);

  /** Dispara o import. Lança o erro da API para a tela tratar (428 sem PDPJ, etc.). */
  const iniciar = useCallback(async () => {
    setDispensado(null);
    setCancelouAtivo(false);
    // Feedback imediato: o aviso de "na fila" aparece no clique, não quando o SSE conectar.
    setConhecido({ status: 'PENDING', fonte: 'manual' });
    const pedido = importarAcervo();
    pedidoEmVoo.current = pedido.catch(() => undefined);
    try {
      await pedido;
    } catch (err) {
      // 409: já há import na fila ou rodando — o aviso continua valendo, só passa a acompanhar.
      if ((err as { response?: { status?: number } })?.response?.status !== 409) {
        setConhecido(null);
        throw err;
      }
    }
    setAcompanhar(true);
    // Reabre o stream: o anterior pode ter fechado num estado terminal de outro job.
    setGeracao((g) => g + 1);
  }, []);

  const dispensar = useCallback(() => {
    lembrarDispensado(progresso?.job_id);
    setDispensado(progresso?.job_id ?? 'sem-id');
  }, [progresso?.job_id]);

  /**
   * Cancela o import. Para quem clicou, é imediato: o aviso passa a "Importação cancelada" e fica
   * assim — o worker só consulta o pedido entre duas buscas no Data Lake e ainda leva alguns
   * segundos para parar, mas o progresso desse intervalo não volta para a tela.
   */
  const cancelar = useCallback(async () => {
    setCancelouAtivo(true);
    try {
      // O pedido de import pode levar segundos (o gate do certificado consulta o autenticador) e o
      // job só existe quando ele termina: cancelar antes disso não acharia nada para cancelar.
      await pedidoEmVoo.current;
      await cancelarImportacaoAcervo();
    } catch (err) {
      // 404: não há import em andamento — é o que o usuário queria, não um erro para mostrar.
      if ((err as { response?: { status?: number } })?.response?.status === 404) return;
      setCancelouAtivo(false);
      throw err;
    }
  }, []);

  const foiDispensado =
    dispensado !== null && (dispensado === 'sem-id' || dispensado === progresso?.job_id);
  const visivel =
    !foiDispensado &&
    progresso !== null &&
    (progresso.status !== 'FAILED' || progresso.fonte === 'manual');

  const emAndamento = progresso !== null && ATIVOS.has(progresso.status);
  // Cancelado por aqui: a tela já dá o import por encerrado, mesmo que o worker ainda publique o
  // progresso de uma página em curso. Quando ele para, o estado real (terminal) volta a valer.
  const paraExibir =
    cancelouAtivo && emAndamento ? { ...progresso, status: 'CANCELLED' as const } : progresso;

  // "Importação cancelada" não tem nada a dizer depois de lido: sai sozinho, sem exigir o clique.
  const dispensarRef = useRef(dispensar);
  useEffect(() => {
    dispensarRef.current = dispensar;
  }, [dispensar]);
  const avisoCancelado = visivel && paraExibir?.status === 'CANCELLED';
  useEffect(() => {
    if (!avisoCancelado) return;
    const timer = setTimeout(() => dispensarRef.current(), AVISO_CANCELADO_MS);
    return () => clearTimeout(timer);
  }, [avisoCancelado]);

  return {
    progresso: visivel ? paraExibir : null,
    emAndamento,
    iniciar,
    cancelar,
    dispensar,
  };
}
